/**
 * alx-label-studio.js - V241 27.10.26 (Susie): full-screen "Label Studio" for personalised labels.
 *
 * A layout and flow change only. Nothing in the personalisation system is rewritten:
 * snippets/personalisation-fields.liquid, assets/personalisation.js and assets/personalisation-preview.js
 * build and run the label box exactly as before. After they have set themselves up (DOMContentLoaded),
 * this file:
 *   1. puts a compact panel on the product page where the label box was: the heading, a short helper
 *      line, a big gold "Personalise Your Label" button and, once a design is chosen, a small summary
 *      (thumbnail of the live preview + design name + name/company text);
 *   2. moves the existing label box ([data-personalisation-fields]) into a full-screen overlay, and its
 *      live preview ([data-personalisation-preview-wrapper]) into the overlay's preview area: big and on
 *      the left on computers (990px+), pinned at the top on phones and tablets;
 *   3. adds tabs at the top of the options ("Choose a Design" / "Professional Designs" / "Upload Your
 *      Logo or Design"). The last two simply open the existing branded-logo-labels and branded-logo
 *      collections (the same tiles that used to sit in the 'Choose A Collection' grid), and only appear
 *      when those collections are offered on the product;
 *   4. adds a footer with the price, a quantity stepper, Add to Basket and Done.
 * Elements are MOVED, never copied, so every listener, the live preview, uploads and validation keep
 * working. Every input keeps its form="product-form-<section>" attribute, so it is still submitted with
 * the product form. The overlay is hidden with visibility (never the hidden attribute), because
 * personalisation.js skips validation for fields inside a [hidden] container.
 * Add to Basket in the Studio clicks the page's real Add to Basket button, so Dawn's product form,
 * personalisation.js validation and the preview export all run as before. If validation blocks the add
 * (from the Studio or from the page's own button), the Studio opens/stays open at the first error.
 * Styles: assets/alx-label-studio.css. Loaded by the 'Personalised Label' block in templates/product.json,
 * only on products that render the label box.
 */
(function () {
  'use strict';
  if (window.__alxLabelStudio) return;
  window.__alxLabelStudio = true;

  var PRO = 'branded-logo-labels';
  var OWN = 'branded-logo';
  var html = document.documentElement;

  // Run only after DOMContentLoaded has fired, so personalisation.js and personalisation-preview.js (which set
  // themselves up on DOMContentLoaded) have found their elements BEFORE anything is moved. A deferred script runs
  // while readyState is already 'interactive' but before DOMContentLoaded, so readyState alone is not enough:
  // the loader in templates/product.json sets window.__alxDCL when DOMContentLoaded fires.
  function ready(fn) {
    var ran = false;
    var once = function () { if (!ran) { ran = true; fn(); } };
    if (window.__alxDCL || document.readyState === 'complete') { once(); return; }
    document.addEventListener('DOMContentLoaded', function () { setTimeout(once, 0); });
    window.addEventListener('load', once);
  }

  function done() {
    window.__alxLabelStudioReady = true;
    html.classList.remove('alx-ls-pending');
  }

  function txt(el) { return el ? (el.textContent || '').replace(/\s+/g, ' ').trim() : ''; }

  function isHidden(el) {
    for (var n = el; n; n = n.parentElement) { if (n.hidden) return true; }
    return false;
  }

  function make(tag, cls, inner) {
    var e = document.createElement(tag);
    if (cls) e.className = cls;
    if (inner != null) e.innerHTML = inner;
    return e;
  }

  var X_SVG = '<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false"><path d="M6 6 L18 18 M18 6 L6 18" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/></svg>';

  function init() {
    var root = Array.prototype.filter.call(document.querySelectorAll('[data-personalisation-fields]'), function (r) {
      return !r.closest('quick-add-modal, .quick-add-modal, product-modal, .alx-studio');
    })[0];
    if (!root) { done(); return; }

    var formId = root.getAttribute('data-personalisation-form-id') || '';
    var form = formId ? document.getElementById(formId) : null;
    var sid = formId.replace(/^product-form-/, '');
    var preview = root.querySelector('[data-personalisation-preview-wrapper]');
    var previewHome = preview ? { parent: preview.parentNode, next: preview.nextSibling } : null;
    var rootHome = { parent: root.parentNode, next: root.nextSibling };
    var launch = null, studio = null;
    // If anything below fails, put the label box back on the page exactly as it was.
    rollback = function () {
      try {
        if (preview && previewHome && preview.parentNode !== previewHome.parent) previewHome.parent.insertBefore(preview, previewHome.next);
        if (root.parentNode !== rootHome.parent) rootHome.parent.insertBefore(root, rootHome.next && rootHome.next.parentNode === rootHome.parent ? rootHome.next : null);
        if (launch && launch.parentNode) launch.parentNode.removeChild(launch);
        if (studio && studio.parentNode) studio.parentNode.removeChild(studio);
        html.classList.remove('alx-studio-open');
      } catch (e) {}
    };
    var canvas = root.querySelector('[data-personalisation-canvas]');
    var headingEl = root.querySelector('.personalisation-fields__heading');

    /* ---------- 1. Compact panel on the product page ---------- */
    launch = make('div', 'alx-ls-launch',
      '<p class="alx-ls-launch__heading"></p>' +
      '<p class="alx-ls-launch__helper"></p>' +
      '<button type="button" class="alx-ls-launch__btn" aria-haspopup="dialog">Personalise Your Label</button>' +
      '<div class="alx-ls-launch__summary" hidden>' +
        '<canvas class="alx-ls-launch__thumb" width="240" height="160" aria-hidden="true"></canvas>' +
        '<div class="alx-ls-launch__summary-text">' +
          '<span class="alx-ls-launch__summary-kicker">Your label</span>' +
          '<span class="alx-ls-launch__summary-title"></span>' +
          '<span class="alx-ls-launch__summary-detail"></span>' +
        '</div>' +
      '</div>');
    root.parentNode.insertBefore(launch, root);
    var launchBtn = launch.querySelector('.alx-ls-launch__btn');
    var summary = launch.querySelector('.alx-ls-launch__summary');
    var thumb = launch.querySelector('.alx-ls-launch__thumb');

    /* ---------- 2. The Studio overlay ---------- */
    studio = make('div', 'alx-studio',
      '<div class="alx-studio__bar">' +
        '<p class="alx-studio__title" id="AlxStudioTitle-' + sid + '"></p>' +
        '<button type="button" class="alx-studio__continue alx-studio__continue--bar">Continue to gift options</button>' +
        '<button type="button" class="alx-studio__close" aria-label="Close" title="Close">' + X_SVG + '</button>' +
      '</div>' +
      '<div class="alx-studio__stage"><div class="alx-studio__stage-inner"></div></div>' +
      '<div class="alx-studio__pane"><div class="alx-studio__tabs" role="group" aria-label="How would you like to personalise?" hidden></div></div>' +
      '<div class="alx-studio__footer">' +
        '<div class="alx-studio__footer-row">' +
          '<div class="alx-studio__price"></div>' +
          '<div class="alx-studio__qty" hidden>' +
            '<span class="alx-studio__qty-label">Qty</span>' +
            '<button type="button" class="alx-studio__qty-btn" data-step="minus" aria-label="Decrease quantity">&minus;</button>' +
            '<span class="alx-studio__qty-val" aria-live="polite">1</span>' +
            '<button type="button" class="alx-studio__qty-btn" data-step="plus" aria-label="Increase quantity">+</button>' +
          '</div>' +
        '</div>' +
        '<p class="alx-studio__msg" role="alert" hidden></p>' +
        '<div class="alx-studio__actions">' +
          '<button type="button" class="alx-studio__done">Done</button>' +
          '<button type="button" class="alx-studio__continue alx-studio__continue--foot">Continue</button>' +
          '<button type="button" class="alx-studio__add"><span class="alx-studio__add-text">Add to Basket</span></button>' +
        '</div>' +
      '</div>');
    studio.setAttribute('role', 'dialog');
    studio.setAttribute('aria-modal', 'true');
    studio.setAttribute('aria-labelledby', 'AlxStudioTitle-' + sid);
    studio.setAttribute('tabindex', '-1');
    document.body.appendChild(studio);

    var titleEl = studio.querySelector('.alx-studio__title');
    var closeBtn = studio.querySelector('.alx-studio__close');
    var stage = studio.querySelector('.alx-studio__stage');
    var stageInner = studio.querySelector('.alx-studio__stage-inner');
    var pane = studio.querySelector('.alx-studio__pane');
    var tabsBox = studio.querySelector('.alx-studio__tabs');
    var footer = studio.querySelector('.alx-studio__footer');
    var priceBox = studio.querySelector('.alx-studio__price');
    var qtyBox = studio.querySelector('.alx-studio__qty');
    var qtyVal = studio.querySelector('.alx-studio__qty-val');
    var msg = studio.querySelector('.alx-studio__msg');
    var doneBtn = studio.querySelector('.alx-studio__done');
    var addBtn = studio.querySelector('.alx-studio__add');
    var addText = studio.querySelector('.alx-studio__add-text');
    var continueBtns = studio.querySelectorAll('.alx-studio__continue');

    // Move (not copy) the live preview to the preview area, and the label box into the options pane.
    if (preview) {
      stageInner.appendChild(preview);
      stageInner.appendChild(make('p', 'alx-studio__caption', 'Your label preview'));
    } else {
      studio.classList.add('alx-studio--single');
    }
    pane.appendChild(root);

    // Colour names as tooltips / accessible names on the round colour swatches.
    root.querySelectorAll('.personalisation-fields__textcolour-option, .personalisation-fields__design-option--swatch').forEach(function (label) {
      var cap = label.querySelector('.personalisation-fields__design-caption');
      var name = txt(cap);
      if (!name) return;
      if (!label.title) label.title = name;
      var input = label.querySelector('input');
      if (input && !input.getAttribute('aria-label')) input.setAttribute('aria-label', name);
    });

    /* ---------- Headings ---------- */
    // V241 (13) 27.10.26 (Susie): the drink in the helper line follows the product - its product type first, then its
    // title: Champagne / Prosecco / wine / alcohol-free / whisky gift, otherwise just 'gift'.
    function giftWord() {
      var meta = window.ShopifyAnalytics && window.ShopifyAnalytics.meta && window.ShopifyAnalytics.meta.product;
      var pTitle = txt(document.querySelector('.product__title h1, .product__title h2, .product__title')) ||
        ((document.querySelector('meta[property="og:title"]') || {}).content || '');
      var pick = function (s) {
        s = String(s || '').toLowerCase();
        if (/alcohol[\s-]*free|non[\s-]*alcoholic/.test(s)) return 'alcohol-free gift';
        if (/champagne/.test(s)) return 'Champagne gift';
        if (/prosecco/.test(s)) return 'Prosecco gift';
        if (/whisk(e)?y/.test(s)) return 'whisky gift';
        if (/\bwines?\b/.test(s)) return 'wine gift';
        return '';
      };
      return pick(meta && meta.type) || pick(pTitle) || 'gift';
    }
    function syncHeading() {
      var h = txt(headingEl) || 'Personalise Your Gift';
      var corporate = /^Brand\b/i.test(h);
      launch.querySelector('.alx-ls-launch__heading').textContent = h;
      var helperEl = launch.querySelector('.alx-ls-launch__helper');
      // V241 (15) 27.10.26 (Susie): two lines, with a line break after the colon.
      var lines = corporate
        ? ['Create a polished corporate gift that’s uniquely yours:', 'upload your logo, add your message and see your label live.']
        : ['Create a thoughtful ' + giftWord() + ' that’s uniquely yours:', 'choose a design, add your message and see your label live.'];
      helperEl.textContent = lines[0];
      helperEl.appendChild(document.createElement('br'));
      helperEl.appendChild(document.createTextNode(lines[1]));
      titleEl.textContent = h;
    }
    syncHeading();
    window.addEventListener('load', syncHeading);

    /* ---------- 3. Tabs ---------- */
    var tilePro = root.querySelector('[data-category-select="' + PRO + '"]');
    var tileOwn = root.querySelector('[data-category-select="' + OWN + '"]');
    var tabDefs = [{ key: 'design', label: 'Choose a Design' }];
    var setMode = function () {};
    if (tilePro) tabDefs.push({ key: 'pro', label: 'Professional Designs' });
    if (tileOwn) tabDefs.push({ key: 'own', label: 'Upload Your Logo or Design' });
    var tabButtons = [];

    function visibleGroup() {
      return Array.prototype.filter.call(root.querySelectorAll('[data-design-group]'), function (g) { return !g.hidden; })[0] || null;
    }
    function currentTab() {
      var g = visibleGroup();
      var h = g ? g.getAttribute('data-design-group') : '';
      return h === PRO ? 'pro' : (h === OWN ? 'own' : 'design');
    }
    function syncTabs() {
      if (typeof fitTiles === 'function') fitTiles();
      syncStep();
      var cur = currentTab();
      studio.setAttribute('data-alx-tab', cur);
      // V245: 'Continue to gift options' only shows once a design is chosen (or a ready-made design is uploaded).
      if (hasDesign()) studio.setAttribute('data-alx-ready', ''); else studio.removeAttribute('data-alx-ready');
      // V246 28.10.26 (Susie): the Ready-Made Designs (branded-logo-labels) are set up as photo-upload designs (the
      // logo goes in the photo area, Name/Message underneath). In the Studio that upload is shown as 'Upload your
      // logo' (CSS on data-alx-pro), always in the with-text mode: a 'Full-Photo Label' choice left over from
      // Photograph Labels is switched off so the logo cannot fill the whole label.
      if (cur === 'pro') {
        studio.setAttribute('data-alx-pro', '');
        var photoOnly = root.querySelector('[data-personalisation-photo-only]');
        if (photoOnly && photoOnly.checked) { photoOnly.checked = false; photoOnly.dispatchEvent(new Event('change')); }
      } else {
        studio.removeAttribute('data-alx-pro');
      }
      if (cur !== 'own') setMode('');
      tabButtons.forEach(function (b) { b.setAttribute('aria-pressed', b.getAttribute('data-tab') === cur ? 'true' : 'false'); });
    }
    function backToPicker() {
      var g = visibleGroup();
      var back = g && g.querySelector('[data-category-back]');
      if (back) back.click();
    }
    function selectTab(key) {
      if (key === currentTab()) return;
      backToPicker();
      if (key === 'pro' && tilePro) tilePro.click();
      if (key === 'own' && tileOwn) tileOwn.click();
      syncTabs();
      pane.scrollTop = 0;
    }
    // V241 (2) 27.10.26 (Susie): PERSONAL products (not custom.branded) have no tabs. The Studio opens on the
    // collection tiles, and a 'For business gifts' section under them opens the corporate groups. Corporate /
    // branded products keep the tabs. Corporate = the personal tiles are hidden by the alx-perfect-pairings CSS
    // (same test as personalisation.js) or the heading reads 'Brand Your ...'.
    var birthdaysTile = root.querySelector('.personalisation-fields__category-grid [data-category-select="birthdays"]');
    var corporate = (!!birthdaysTile && getComputedStyle(birthdaysTile).display === 'none') || /^Brand\b/i.test(txt(headingEl));
    var BACK_TEXT = '← Back to occasion designs';
    setMode = function (mode) {
      if (mode) studio.setAttribute('data-alx-mode', mode); else studio.removeAttribute('data-alx-mode');
    };
    function renameProHeading() {
      var legend = root.querySelector('[data-design-group="' + PRO + '"] .personalisation-fields__designs-legend');
      if (!legend) return;
      var texts = Array.prototype.filter.call(legend.childNodes, function (n) { return n.nodeType === 3 && n.textContent.trim(); });
      if (!texts.length) { legend.appendChild(document.createTextNode('Our Ready-Made Designs')); return; }
      texts[0].textContent = 'Our Ready-Made Designs';
      texts.slice(1).forEach(function (n) { n.textContent = ''; });
    }
    if (!corporate && (tilePro || tileOwn)) {
      tabDefs = [{ key: 'design', label: '' }];
      var picker = root.querySelector('[data-category-picker]');
      var biz = make('div', 'alx-biz',
        '<p class="alx-biz__heading">For business gifts</p>' +
        // V241 (7) 27.10.26 (Susie): the three buttons share the collection tile class, so they look exactly like the tiles.
        // V241 (8) 27.10.26 (Susie): two lines on each button - the name, and a smaller gold line under it.
        '<div class="alx-biz__grid">' +
          (tilePro ? '<button type="button" class="alx-biz__btn personalisation-fields__category-tile" data-alx-biz="pro"><span class="alx-biz__l1">Our Ready-Made Designs</span><span class="alx-biz__l2">Upload Your Logo</span></button>' : '') +
          (tileOwn ? '<button type="button" class="alx-biz__btn personalisation-fields__category-tile" data-alx-biz="logo"><span class="alx-biz__l1">Design Your Own Label</span><span class="alx-biz__l2">Upload Your Logo</span></button>' : '') +
          (tileOwn ? '<button type="button" class="alx-biz__btn personalisation-fields__category-tile" data-alx-biz="artwork"><span class="alx-biz__l1">Your Ready-Made Artwork</span><span class="alx-biz__l2">Upload Your Design</span></button>' : '') +
        '</div>');
      if (picker) picker.appendChild(biz);
      // V241 (3) 27.10.26 (Susie): the office collections sit under 'For business gifts', above the three buttons,
      // in the same tile style as the occasion grid. Awards & Recognition, Office Celebrations and Retirement are
      // MOVED there (their own tiles, so every existing listener still works) and no longer show in the occasion grid.
      var OFFICE = ['awards-promotions', 'office-celebrations', 'retirement'];
      var officeGrid = make('div', 'personalisation-fields__category-grid alx-biz__tiles');
      OFFICE.forEach(function (h) {
        var tile = root.querySelector('.personalisation-fields__category-grid:not(.alx-biz__tiles) [data-category-select="' + h + '"]');
        if (tile) officeGrid.appendChild(tile);
      });
      if (officeGrid.children.length) biz.insertBefore(officeGrid, biz.querySelector('.alx-biz__grid'));
      biz.addEventListener('click', function (e) {
        var b = e.target.closest('[data-alx-biz]');
        if (!b) return;
        var which = b.getAttribute('data-alx-biz');
        backToPicker();
        if (which === 'pro') { tilePro.click(); setMode(''); }
        else { tileOwn.click(); setMode(which === 'artwork' ? 'artwork' : 'logo'); }
        // V244 (3) 27.10.26 (Susie): 'Design Your Own Label' no longer shows the ready-made design upload (it has its own
        // button), so a file left there from 'Your Ready-Made Artwork' is cleared - otherwise it would silently replace
        // the logo label. personalisation-preview.js resets the preview from the input's own 'change' event.
        if (which === 'logo') {
          var ownIn = root.querySelector('[data-personalisation-owndesign-input]');
          if (ownIn && ownIn.files && ownIn.files.length) {
            ownIn.value = '';
            var ownName = ownIn.closest('.personalisation-fields__file-field');
            ownName = ownName && ownName.querySelector('[data-file-name]');
            if (ownName) ownName.textContent = 'No file chosen';
            ownIn.dispatchEvent(new Event('change'));
          }
          var ownErrEl = ownIn && document.getElementById(ownIn.getAttribute('data-error-target'));
          if (ownErrEl) ownErrEl.classList.add('hidden');
        }
        syncTabs();
        pane.scrollTop = 0;
        if (which === 'artwork') {
          var ownBtn = root.querySelector('label[for="' + ((root.querySelector('[data-personalisation-owndesign-input]') || {}).id || '-') + '"]');
          if (ownBtn) setTimeout(function () { try { ownBtn.focus({ preventScroll: true }); } catch (err) {} }, 60);
        }
      });
      // 'Change collection' inside the three business groups reads 'Back to occasion designs'.
      [PRO, OWN, 'awards-promotions', 'office-celebrations', 'retirement'].forEach(function (h) {
        var back = root.querySelector('[data-design-group="' + h + '"] [data-category-back]');
        if (back) back.textContent = BACK_TEXT;
      });
      renameProHeading();
      window.addEventListener('load', renameProHeading);
      studio.classList.add('alx-studio--biz');
    }
    // V241 (5) 27.10.26 (Susie): collection names on ONE line in shorter tiles. The two-line breaks added by
    // personalisation.js (V204) become plain spaces here, and fitTiles() picks the largest font size (17px down to
    // 15px on computers, 16px down to 14px on phones/tablets) at which every visible tile fits on one line; any tile
    // that still does not fit at the smallest size is allowed to wrap. Sizes/heights are in assets/alx-label-studio.css.
    root.querySelectorAll('.personalisation-fields__category-tile br').forEach(function (br) {
      br.parentNode.replaceChild(document.createTextNode(' '), br);
    });
    // V241 (8) 27.10.26 (Susie): on computers each line of the business buttons stays on one line, the same size on all
    // three buttons. Line 1 starts at the tile size and may come down by up to 1.5px; line 2 starts at 85% of it and may
    // come down to about 72%. A line that still cannot fit wraps. Phones and tablets wrap naturally.
    function fitBiz() {
      var desktop = window.matchMedia('(min-width: 990px)').matches;
      [['.alx-biz__l1', 1, 1.5], ['.alx-biz__l2', 0.85, 0.13]].forEach(function (cfg) {
        var spans = Array.prototype.slice.call(root.querySelectorAll('.alx-biz__btn ' + cfg[0]));
        spans.forEach(function (s) { s.style.removeProperty('font-size'); s.classList.remove('alx-biz__wrap'); });
        if (!desktop || !spans.length || !spans[0].clientWidth) return;
        var base = parseFloat(getComputedStyle(spans[0].parentNode).fontSize) || 16;
        var fs = base * cfg[1], min = cfg[0] === '.alx-biz__l1' ? base - cfg[2] : base * (cfg[1] - cfg[2]);
        var over = function (s) { return s.scrollWidth > s.clientWidth + 1; };
        while (spans.some(over) && fs > min) {
          fs = Math.max(min, fs - 0.5);
          spans.forEach(function (s) { s.style.setProperty('font-size', fs + 'px', 'important'); });
        }
        spans.forEach(function (s) { if (over(s)) s.classList.add('alx-biz__wrap'); });
      });
    }
    function fitTiles() { fitGrid(); fitBiz(); }
    function fitGrid() {
      var tiles = Array.prototype.filter.call(root.querySelectorAll('.personalisation-fields__category-grid .personalisation-fields__category-tile'), function (t) {
        return t.clientWidth > 0 && getComputedStyle(t).display !== 'none';
      });
      if (!tiles.length) return;
      var desktop = window.matchMedia('(min-width: 990px)').matches;
      var size = desktop ? 17 : 16, min = desktop ? 15 : 14;
      var over = function (t) { return t.scrollWidth > t.clientWidth + 1; };
      var fits = function () {
        for (var fs = size; fs >= min; fs -= 0.5) {
          root.style.setProperty('--alx-tile-fs', fs + 'px');
          if (!tiles.some(over)) return true;
        }
        return false;
      };
      tiles.forEach(function (t) { t.classList.remove('alx-tile-wrap'); });
      // Computers: 3 across; on a narrow computer screen where even 15px will not fit, 2 across instead of wrapping.
      root.classList.remove('alx-tiles-2');
      if (fits()) return;
      if (desktop) { root.classList.add('alx-tiles-2'); if (fits()) return; }
      root.style.setProperty('--alx-tile-fs', min + 'px');
      tiles.forEach(function (t) { if (over(t)) t.classList.add('alx-tile-wrap'); });
    }
    /* ---------- V241 (9) 27.10.26 (Susie): PERSONAL products, one step at a time ----------
       Start: a short intro, the collection tiles and 'For business gifts'; no text or upload fields.
       Collection chosen: '<- Back to collections' and that collection's designs; still no fields.
       Design chosen (or the ready-made artwork upload): that design's fields appear under the designs, the options
       panel glides down to them (no glide with reduced motion) and the first field is focused without a jump.
       The fields are only hidden with CSS (data-alx-step on the Studio); nothing is cleared. Where the existing preview
       script clears one set of fields when switching between the standard and Design Your Own Label paths, the typed
       text (and an uploaded logo) is put back when the customer returns to that path. While no design is chosen the
       preview shows a placeholder. Branded (corporate) products are unchanged. */
    var steps = !corporate;
    var designPicker = root.querySelector('.personalisation-fields__design-picker');
    var designErr = designPicker ? document.getElementById(designPicker.getAttribute('data-error-target') || '-') : null;
    var designErrHome = designErr ? { parent: designErr.parentNode, next: designErr.nextSibling } : null;
    var stdBox = root.querySelector('[data-fields-standard]');
    var brBox = root.querySelector('[data-fields-branded]');
    var stepPicker = root.querySelector('[data-category-picker]');
    var intro = null, ph = null, lastStep = '';
    var reduceMotion = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
    function hasDesign() {
      if (root.querySelector('input[name="properties[Label Design]"]:checked')) return true;
      var own = root.querySelector('[data-personalisation-owndesign-input]');
      return !!(own && own.files && own.files.length);
    }
    function placePh() {
      if (!ph || !canvas) return;
      var c = canvas.getBoundingClientRect(), s = stageInner.getBoundingClientRect();
      ph.style.left = (c.left - s.left) + 'px';
      ph.style.top = (c.top - s.top) + 'px';
      ph.style.width = c.width + 'px';
      ph.style.height = c.height + 'px';
    }
    function currentStep() {
      var g = visibleGroup();
      if (!g) return 'start';
      if (studio.getAttribute('data-alx-mode') === 'artwork') return 'fields';
      return g.querySelector('input[type="radio"]:checked') ? 'fields' : 'designs';
    }
    function syncStep() {
      if (!steps) return;
      var st = currentStep();
      studio.setAttribute('data-alx-step', st);
      // 'Please choose a label design.' sits above the collections at the start, and under the designs after that.
      if (designErr && stepPicker) {
        if (st === 'start') {
          if (designErr.parentNode !== stepPicker) stepPicker.insertBefore(designErr, intro ? intro.nextSibling : stepPicker.firstChild);
        } else if (designErr.parentNode !== designErrHome.parent) {
          designErrHome.parent.insertBefore(designErr, designErrHome.next && designErrHome.next.parentNode === designErrHome.parent ? designErrHome.next : null);
        }
      }
      if (lastStep && st !== lastStep && (st === 'start' || lastStep === 'start')) pane.scrollTop = 0;
      lastStep = st;
      if (ph) { ph.hidden = hasDesign(); placePh(); }
    }
    function firstField() {
      var box = [stdBox, brBox].filter(function (b) { return b && !b.hidden; })[0];
      if (!box) return null;
      return Array.prototype.filter.call(box.querySelectorAll('input:not([type="hidden"]):not([type="radio"]):not([type="checkbox"]), textarea, select'), function (n) {
        return !n.disabled && !isHidden(n) && n.getClientRects().length && getComputedStyle(n.closest('.personalisation-fields__field, .field') || n).display !== 'none';
      })[0] || null;
    }
    function revealFields() {
      var f = firstField();
      if (!f) return;
      var box = f.closest('[data-photo-upload-field]') || f.closest('.personalisation-fields__field, .field') || f;
      var pr = pane.getBoundingClientRect(), br = box.getBoundingClientRect();
      if (br.top < pr.top + 8 || br.bottom > pr.bottom - 8) {
        var top = pane.scrollTop + (br.top - pr.top) - Math.min(120, pr.height * 0.25);
        var smooth = !(reduceMotion && reduceMotion.matches);
        try { pane.scrollTo({ top: top, behavior: smooth ? 'smooth' : 'auto' }); } catch (e) { pane.scrollTop = top; }
      }
      try { f.focus({ preventScroll: true }); } catch (e) {}
    }
    if (steps) {
      studio.classList.add('alx-studio--steps');
      root.querySelectorAll('[data-design-group] [data-category-back]').forEach(function (b) { b.textContent = '← Choose another occasion'; });
      // V241 (11) 27.10.26 (Susie): 'Choose A Collection' reads 'For Personal Gifts' (same style; shown in capitals).
      var pickLegend = root.querySelector('.personalisation-fields__category-picker-legend');
      var renamePick = function () { if (pickLegend && txt(pickLegend) !== 'For Personal Gifts') pickLegend.textContent = 'For Personal Gifts'; };
      renamePick();
      window.addEventListener('load', renamePick);
      intro = make('div', 'alx-intro',
        '<p class="alx-intro__title">Create your personalised label</p>' +
        '<div class="alx-intro__box"><ol class="alx-intro__steps">' +
          '<li><span class="alx-intro__n">1</span><span>Choose an occasion</span></li>' +
          '<li><span class="alx-intro__n">2</span><span>Pick your design</span></li>' +
          '<li><span class="alx-intro__n">3</span><span>Add your name and message, and watch your label appear <span class="alx-intro__where">on the left</span><span class="alx-intro__where-m">above</span>.</span></li>' +
        '</ol></div>');
      if (stepPicker) stepPicker.insertBefore(intro, stepPicker.firstChild);
      if (preview && canvas) {
        ph = make('div', 'alx-studio__ph', '<p class="alx-studio__ph-title">Your label will appear here</p><p class="alx-studio__ph-sub">Choose an occasion and design to begin</p>');
        ph.setAttribute('aria-hidden', 'true');
        stageInner.appendChild(ph);
        if (typeof ResizeObserver !== 'undefined') new ResizeObserver(placePh).observe(canvas);
        window.addEventListener('resize', placePh);
      }
      // A design picked (a tap, the larger-designs window, or a one-design collection opening): show its fields.
      // Design Your Own Label opens with its White background already chosen, so its fields simply show.
      root.addEventListener('change', function (e) {
        var t = e.target;
        if (!isOpen || !t || t.name !== 'properties[Label Design]' || !t.checked) return;
        var g = t.closest('[data-design-group]');
        if (!g || g.hidden || (!e.isTrusted && g.getAttribute('data-design-group') === OWN)) return;
        setTimeout(function () { syncStep(); revealFields(); }, 40);
      });
      // Keep what the customer typed when the preview script clears a path's fields on a switch.
      var kept = {}, keptLogo = null;
      var keyOf = function (i) { return i.id || i.name; };
      root.addEventListener('input', function (e) {
        var t = e.target;
        if (t && t.matches && t.matches('input[type="text"], textarea')) kept[keyOf(t)] = t.value;
      }, true);
      root.addEventListener('change', function (e) {
        var t = e.target;
        if (!t || !t.matches || !t.matches('[data-personalisation-logo-input]') || !t.files || !t.files.length) return;
        try { var dt = new DataTransfer(); Array.prototype.forEach.call(t.files, function (f) { dt.items.add(f); }); keptLogo = dt.files; } catch (err) {}
      }, true);
      var pathNow = function () { return brBox && !brBox.hidden ? 'branded' : 'standard'; };
      var restoreTyped = function () {
        Object.keys(kept).forEach(function (k) {
          var i = document.getElementById(k) || root.querySelector('[name="' + k + '"]');
          if (!i || !root.contains(i) || !kept[k] || i.value || i.disabled || isHidden(i)) return;
          if (getComputedStyle(i.closest('.personalisation-fields__field, .field') || i).display === 'none') return;
          i.value = kept[k];
          i.dispatchEvent(new Event('input')); // not bubbling: redraws the preview without re-running the error checks
        });
        var logo = root.querySelector('[data-personalisation-logo-input]');
        if (keptLogo && keptLogo.length && logo && !logo.disabled && !isHidden(logo) && !(logo.files && logo.files.length)) {
          try { logo.files = keptLogo; logo.dispatchEvent(new Event('change')); } catch (err) {}
        }
      };
      root.addEventListener('click', function (e) {
        if (!e.target.closest || !e.target.closest('[data-category-select], [data-category-back]')) return;
        var before = pathNow();
        setTimeout(function () { if (pathNow() !== before) restoreTyped(); }, 0);
      }, true);
    }
    // V244 (4) 27.10.26 (Susie): in the Studio, 'Show N more designs' moves up beside 'View all designs larger' (one
    // centred row above the designs; on phones, where View all is hidden, it sits there alone). Clicking it still
    // reveals the rest of the designs; once it is hidden (by its own click or by picking an extra design in the larger
    // view) both buttons go back where they were, so 'View all designs larger' sits alone at the top again.
    root.querySelectorAll('[data-show-more-designs]').forEach(function (more) {
      var grid = more.previousElementSibling;
      var group = more.closest('[data-design-group]');
      if (more.hidden || !group || !grid || !grid.classList.contains('personalisation-fields__design-grid')) return;
      var viewAll = Array.prototype.filter.call(group.children, function (c) { return c.hasAttribute('data-view-all-designs'); })[0] || null;
      var row = make('div', 'alx-design-actions');
      grid.parentNode.insertBefore(row, grid);
      if (viewAll) row.appendChild(viewAll);
      row.appendChild(more);
      more.addEventListener('click', function () { grid.removeAttribute('data-design-grid-collapsed'); });
      var restore = function () {
        if (!more.hidden || !row.parentNode) return;
        if (viewAll) row.parentNode.insertBefore(viewAll, row);
        grid.parentNode.insertBefore(more, grid.nextSibling);
        row.parentNode.removeChild(row);
      };
      new MutationObserver(restore).observe(more, { attributes: true, attributeFilter: ['hidden'] });
    });
    if (tabDefs.length > 1) {
      tabDefs.forEach(function (t) {
        var b = make('button', 'alx-studio__tab');
        b.type = 'button';
        b.setAttribute('data-tab', t.key);
        b.textContent = t.label;
        b.addEventListener('click', function () { selectTab(t.key); });
        tabsBox.appendChild(b);
        tabButtons.push(b);
      });
      tabsBox.hidden = false;
      studio.classList.add('alx-studio--tabs');
    }
    syncTabs();
    root.addEventListener('click', function () { setTimeout(syncTabs, 0); });
    root.addEventListener('change', function () { setTimeout(syncTabs, 0); });

    /* ---------- 4. Footer: price, quantity, Add to Basket ---------- */
    var priceSrc = sid ? document.getElementById('price-' + sid) : null;
    function syncPrice() {
      if (!priceSrc) { priceBox.hidden = true; return; }
      priceBox.innerHTML = priceSrc.innerHTML;
      priceBox.querySelectorAll('[id]').forEach(function (n) { n.removeAttribute('id'); });
    }
    syncPrice();
    if (priceSrc) new MutationObserver(syncPrice).observe(priceSrc, { childList: true, subtree: true, characterData: true });

    var qtyInput = sid ? document.getElementById('Quantity-' + sid) : null;
    var qtyWrap = qtyInput ? qtyInput.closest('quantity-input') : null;
    function syncQty() { if (qtyInput) qtyVal.textContent = qtyInput.value || '1'; }
    if (qtyInput && qtyWrap) {
      qtyBox.hidden = false;
      syncQty();
      qtyInput.addEventListener('change', syncQty);
      qtyInput.addEventListener('input', syncQty);
      qtyBox.querySelectorAll('[data-step]').forEach(function (b) {
        b.addEventListener('click', function () {
          var real = qtyWrap.querySelector('button[name="' + b.getAttribute('data-step') + '"]');
          if (real) real.click();
          setTimeout(syncQty, 0);
        });
      });
    }

    var productForm = form ? form.closest('product-form') : null;
    var realBtn = form ? form.querySelector('button[name="add"], button[type="submit"]') : null;
    function syncButton() {
      if (!realBtn) return;
      var off = realBtn.disabled || realBtn.getAttribute('aria-disabled') === 'true';
      addBtn.disabled = !!off;
      addBtn.classList.toggle('is-loading', realBtn.classList.contains('loading'));
      var label = '';
      realBtn.querySelectorAll('span').forEach(function (s) {
        if (!label && !s.classList.contains('hidden') && !s.closest('.loading__spinner') && txt(s)) label = txt(s);
      });
      addText.textContent = (!label || /^add to (cart|basket|bag)$/i.test(label)) ? 'Add to Basket' : label;
    }
    syncButton();
    if (realBtn) new MutationObserver(syncButton).observe(realBtn, { attributes: true, childList: true, subtree: true, characterData: true });

    function showMsg(t) { msg.textContent = t; msg.hidden = !t; }

    // Dawn's own add-to-basket errors (sold out, stock limits) appear on the page; repeat them here.
    var dawnErr = productForm ? productForm.querySelector('.product-form__error-message-wrapper') : null;
    if (dawnErr) {
      new MutationObserver(function () {
        if (!dawnErr.hidden) showMsg(txt(dawnErr.querySelector('.product-form__error-message')) || 'Sorry, this could not be added to your basket.');
      }).observe(dawnErr, { attributes: true, attributeFilter: ['hidden'], childList: true, subtree: true });
    }

    addBtn.addEventListener('click', function () {
      showMsg('');
      if (realBtn) {
        if (realBtn.disabled || realBtn.getAttribute('aria-disabled') === 'true') return;
        realBtn.click();
      } else if (form && form.requestSubmit) {
        form.requestSubmit();
      }
    });

    /* ---------- Validation: open the Studio at the first error ---------- */
    function firstError() {
      var list = root.querySelectorAll('.form__message:not(.hidden)');
      for (var i = 0; i < list.length; i++) { if (!isHidden(list[i]) && list[i].getClientRects().length) return list[i]; }
      return null;
    }
    function goToError(err) {
      var field = err.id ? root.querySelector('[data-error-target="' + err.id + '"]') : null;
      if (steps && err === designErr) field = null;
      var target = (field && !isHidden(field) && field.getClientRects().length) ? field : err;
      target.scrollIntoView({ block: 'center' });
      if (field && !/^(INPUT|SELECT|TEXTAREA|BUTTON)$/.test(field.tagName)) {
        field = Array.prototype.filter.call(field.querySelectorAll('button, input:not([type="hidden"])'), function (n) {
          return !n.disabled && n.getClientRects().length && getComputedStyle(n).display !== 'none';
        })[0] || null;
      }
      if (field && typeof field.focus === 'function' && field.getClientRects().length) {
        try { field.focus({ preventScroll: true }); } catch (e) { field.focus(); }
      }
    }
    /* ---------- V241 (12) 27.10.26 (Susie): Add to Basket with no label design ----------
       Instead of 'Please choose a label design.', a small dialog asks whether to personalise the label or carry on
       without one (Personalise My Label opens the Studio at the start). 'Continue Without a Label' (only once the tick
       box is ticked) adds the gift through the real Add to Basket with 'Personalised Label: Not required (customer
       confirmed)' (branded products: 'Branded Label: ...') and a hidden '_No Label Confirmed: yes'. For that one add the
       label fields are disabled (so no design, name, message, preview or reference is sent) and the label box counts as
       hidden (so personalisation.js skips its design check); everything is put back straight after, so the next add
       starts fresh. A chosen design with missing details still shows the usual errors in the Studio. */
    var noLabel = null, noLabelBypass = false, noLabelReturn = null;
    if (form && designPicker) {
      var NL = corporate
        ? { title: 'No branded label has been created', ok: 'I&#39;m happy for my gift to be sent without a branded label', prop: 'Branded Label' }
        : { title: 'No personalised label has been created', ok: 'I&#39;m happy for my gift to be sent without a personalised label', prop: 'Personalised Label' };
      noLabel = make('div', 'alx-nolabel',
        '<div class="alx-nolabel__backdrop" data-alx-nolabel-close></div>' +
        '<div class="alx-nolabel__box" role="dialog" aria-modal="true" aria-labelledby="AlxNoLabelTitle-' + sid + '" aria-describedby="AlxNoLabelText-' + sid + '">' +
          '<button type="button" class="alx-nolabel__close" data-alx-nolabel-close aria-label="Close" title="Close">' + X_SVG + '</button>' +
          '<p class="alx-nolabel__title" id="AlxNoLabelTitle-' + sid + '">' + NL.title + '</p>' +
          '<p class="alx-nolabel__text" id="AlxNoLabelText-' + sid + '">Would you like to personalise your label, or continue without one?</p>' +
          '<button type="button" class="alx-nolabel__personalise">Personalise My Label</button>' +
          '<div class="alx-nolabel__check"><input type="checkbox" id="AlxNoLabelOk-' + sid + '"><label for="AlxNoLabelOk-' + sid + '">' + NL.ok + '</label></div>' +
          '<button type="button" class="alx-nolabel__continue" disabled>Continue Without a Label</button>' +
        '</div>');
      noLabel.hidden = true;
      document.body.appendChild(noLabel);
      var nlBox = noLabel.querySelector('.alx-nolabel__box');
      var nlOk = noLabel.querySelector('input[type="checkbox"]');
      var nlGo = noLabel.querySelector('.alx-nolabel__continue');
      var nlPers = noLabel.querySelector('.alx-nolabel__personalise');
      var openNoLabel = function () {
        noLabelReturn = document.activeElement;
        nlOk.checked = false;
        nlGo.disabled = true;
        noLabel.hidden = false;
        html.classList.add('alx-nolabel-open');
        setTimeout(function () { try { nlPers.focus(); } catch (e) {} }, 30);
      };
      var closeNoLabel = function (refocus) {
        if (noLabel.hidden) return;
        noLabel.hidden = true;
        html.classList.remove('alx-nolabel-open');
        nlOk.checked = false;
        nlGo.disabled = true;
        if (refocus && noLabelReturn && document.contains(noLabelReturn)) {
          try { noLabelReturn.focus({ preventScroll: true }); } catch (e) {}
        }
      };
      noLabel.addEventListener('click', function (e) { if (e.target.closest('[data-alx-nolabel-close]')) closeNoLabel(true); });
      nlOk.addEventListener('change', function () { nlGo.disabled = !nlOk.checked; });
      nlPers.addEventListener('click', function () {
        var wasOpen = isOpen;
        closeNoLabel(false);
        open();
        backToPicker();
        if (tabButtons.length) selectTab('design');
        syncTabs();
        pane.scrollTop = 0;
        if (wasOpen) setTimeout(function () { try { closeBtn.focus(); } catch (e) {} }, 40);
      });
      nlGo.addEventListener('click', function () {
        if (nlGo.disabled) return;
        closeNoLabel(false);
        if (realBtn && (realBtn.disabled || realBtn.getAttribute('aria-disabled') === 'true')) return;
        var off = [];
        // The label box and its live preview (moved into the Studio; it holds the preview image and label reference).
        [root, preview].forEach(function (box) {
          if (!box) return;
          box.querySelectorAll('input, select, textarea').forEach(function (i) {
            if (i.name && !i.disabled) { i.disabled = true; off.push(i); }
          });
        });
        var extra = [[NL.prop, 'Not required (customer confirmed)'], ['_No Label Confirmed', 'yes']].map(function (pr) {
          var i = document.createElement('input');
          i.type = 'hidden';
          i.name = 'properties[' + pr[0] + ']';
          i.value = pr[1];
          form.appendChild(i);
          return i;
        });
        var wasHidden = root.hidden;
        noLabelBypass = true;
        root.hidden = true;
        try {
          if (realBtn) realBtn.click();
          else if (form.requestSubmit) form.requestSubmit();
        } finally {
          noLabelBypass = false;
          root.hidden = wasHidden;
          off.forEach(function (i) { i.disabled = false; });
          extra.forEach(function (i) { if (i.parentNode) i.parentNode.removeChild(i); });
        }
      });
      document.addEventListener('keydown', function (e) {
        if (noLabel.hidden) return;
        if (e.key === 'Escape') { e.preventDefault(); e.stopImmediatePropagation(); closeNoLabel(true); return; }
        if (e.key !== 'Tab') return;
        var list = Array.prototype.filter.call(nlBox.querySelectorAll('button, input'), function (n) { return !n.disabled; });
        if (!list.length) return;
        var first = list[0], last = list[list.length - 1];
        if (!nlBox.contains(document.activeElement)) { e.preventDefault(); first.focus(); return; }
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }, true);
      // Runs before personalisation.js (document, capture) and Dawn's product form: no design at all -> the dialog.
      window.addEventListener('submit', function (e) {
        if (e.target !== form || noLabelBypass || isHidden(root) || hasDesign()) return;
        e.preventDefault();
        e.stopImmediatePropagation();
        openNoLabel();
      }, true);
    }

    if (form) {
      // Window capture runs before personalisation.js's document listener; we only look afterwards.
      window.addEventListener('submit', function (e) {
        if (e.target !== form) return;
        setTimeout(function () {
          if (studio.getAttribute('data-alx-mode') === 'artwork') {
            var own = root.querySelector('[data-personalisation-owndesign-input]');
            var ownErr = own && document.getElementById(own.getAttribute('data-error-target'));
            if (ownErr) ownErr.classList.toggle('hidden', !!(own.files && own.files.length));
          }
          // Fields not shown yet (no design chosen) keep their error messages to themselves.
          if (steps && studio.getAttribute('data-alx-step') !== 'fields') {
            [stdBox, brBox].forEach(function (b) { if (b) b.querySelectorAll('.form__message').forEach(function (m) { m.classList.add('hidden'); }); });
          }
          var err = firstError();
          if (!err) return;
          var wasOpen = isOpen;
          if (!wasOpen) open();
          showMsg('Please complete the highlighted details for your label.');
          setTimeout(function () { goToError(err); }, wasOpen ? 0 : 80);
        }, 0);
      }, true);
    }

    // Close the Studio once the gift is in the basket (Dawn publishes cartUpdate from the product form).
    try {
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS === 'object' && PUB_SUB_EVENTS.cartUpdate) {
        subscribe(PUB_SUB_EVENTS.cartUpdate, function (ev) { if (ev && ev.source === 'product-form' && isOpen) close(true); });
      }
    } catch (e) {}
    var drawer = document.querySelector('cart-drawer');
    if (drawer) new MutationObserver(function () { if (isOpen && drawer.classList.contains('active')) close(true); }).observe(drawer, { attributes: true, attributeFilter: ['class'] });

    /* ---------- Summary on the page ---------- */
    var sumTimer = null;
    function textValue(name) {
      var f = root.querySelector('input[name="properties[' + name + ']"]');
      return (f && !f.disabled && !isHidden(f) && (f.value || '').trim()) || '';
    }
    function updateSummary() {
      var checked = root.querySelector('input[name="properties[Label Design]"]:checked');
      var own = root.querySelector('[data-personalisation-owndesign-input]');
      var ownOn = own && !own.disabled && own.files && own.files.length;
      var title = ownOn ? 'Your own design (uploaded)' : (checked ? checked.value : '');
      if (!title) { summary.hidden = true; return; }
      var detail = textValue('Name') || textValue('Company Name');
      summary.querySelector('.alx-ls-launch__summary-title').textContent = title;
      summary.querySelector('.alx-ls-launch__summary-detail').textContent = detail ? '“' + detail + '”' : '';
      summary.hidden = false;
      if (canvas && canvas.width && canvas.height) {
        var ctx = thumb.getContext('2d');
        ctx.clearRect(0, 0, thumb.width, thumb.height);
        try { ctx.drawImage(canvas, 0, 0, thumb.width, thumb.height); } catch (e) {}
      }
      if (!firstError()) showMsg('');
    }
    function queueSummary() { clearTimeout(sumTimer); sumTimer = setTimeout(updateSummary, 400); }
    root.addEventListener('input', queueSummary);
    root.addEventListener('change', queueSummary);
    if (preview) preview.addEventListener('pointerup', queueSummary);

    /* ---------- Open / close, focus trap, Esc ---------- */
    var isOpen = false;
    var lastFocus = null;
    function measure() {
      studio.style.setProperty('--alx-prev-h', (stage.offsetHeight || 0) + 'px');
      studio.style.setProperty('--alx-foot-h', (footer.offsetHeight || 0) + 'px');
    }
    if (typeof ResizeObserver !== 'undefined') {
      var ro = new ResizeObserver(measure);
      ro.observe(stage);
      ro.observe(footer);
    }
    window.addEventListener('resize', function () { measure(); fitTiles(); });
    window.addEventListener('load', fitTiles);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(fitTiles).catch(function () {});

    function open() {
      if (isOpen) return;
      lastFocus = document.activeElement;
      syncHeading(); syncPrice(); syncQty(); syncButton(); syncTabs();
      studio.classList.add('is-open');
      html.classList.add('alx-studio-open');
      isOpen = true;
      measure();
      setTimeout(function () { measure(); closeBtn.focus(); }, 40);
    }
    function close(afterAdd) {
      if (!isOpen) return;
      studio.classList.remove('is-open');
      html.classList.remove('alx-studio-open');
      isOpen = false;
      showMsg('');
      updateSummary();
      if (!afterAdd) {
        var back = launchBtn;
        try { back.focus({ preventScroll: true }); } catch (e) { back.focus(); }
      }
    }
    launchBtn.addEventListener('click', open);
    closeBtn.addEventListener('click', function () { close(false); });
    doneBtn.addEventListener('click', function () { close(false); });

    /* ---------- V245 28.10.26 (Susie): 'Continue to gift options' ----------
       In the Studio's top bar (centred; on phones a 'Continue' button in the footer, in place of Done, next to Add to
       Basket), once a design is chosen. It runs the same checks as Add to Basket without adding anything: a test submit
       event goes through the usual listeners (the no-label check, personalisation.js validation and the Studio's own
       error handling) and is stopped just before Dawn's product form. If a detail is missing the Studio stays open on
       the first error, as for Add to Basket. Otherwise it closes like Done (every choice kept, summary updated) and the
       page glides to the Finishing Touches prompt above Add to Basket (or to Add to Basket when there is no prompt). */
    var probe = false, probeReached = false;
    if (form) {
      // Capture on <html>: after every document/window capture listener (validation), before the form's own listeners.
      html.addEventListener('submit', function (e) {
        if (!probe || e.target !== form) return;
        probeReached = true;
        e.preventDefault();
        e.stopImmediatePropagation();
      }, true);
    }
    function continueTarget() {
      var ftp = document.querySelector('[data-alx-ftp]');
      if (ftp && !ftp.hidden && ftp.getClientRects().length) return ftp;
      if (realBtn && realBtn.getClientRects().length) return realBtn;
      return launch;
    }
    function continueToOptions() {
      showMsg('');
      if (!hasDesign()) return;
      if (form) {
        probe = true; probeReached = false;
        try { form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true })); } finally { probe = false; }
        if (!probeReached) return; // a required detail is missing: the Studio shows it (see the submit listener above)
      }
      close(true);
      try { launchBtn.focus({ preventScroll: true }); } catch (e) {}
      var target = continueTarget();
      var smooth = !(reduceMotion && reduceMotion.matches);
      setTimeout(function () {
        try { target.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto', block: 'center' }); } catch (e) { target.scrollIntoView(); }
      }, 30);
    }
    Array.prototype.forEach.call(continueBtns, function (b) { b.addEventListener('click', continueToOptions); });

    function otherDialogOpen() {
      if (noLabel && !noLabel.hidden) return true;
      if (html.classList.contains('personalisation-gallery-open')) return true;
      var notice = root.querySelector('[data-text-colour-notice]');
      if (notice && !notice.hidden) return true;
      return !!document.querySelector('.personalisation-fields__lightbox');
    }
    function focusables() {
      return Array.prototype.filter.call(
        studio.querySelectorAll('a[href], button, input, select, textarea, [tabindex]:not([tabindex="-1"])'),
        function (n) { return !n.disabled && n.getClientRects().length && getComputedStyle(n).visibility !== 'hidden'; }
      );
    }
    document.addEventListener('keydown', function (e) {
      if (!isOpen || otherDialogOpen()) return;
      if (e.key === 'Escape') { e.preventDefault(); close(false); return; }
      if (e.key !== 'Tab') return;
      var list = focusables();
      if (!list.length) return;
      var first = list[0], last = list[list.length - 1];
      if (!studio.contains(document.activeElement)) { e.preventDefault(); first.focus(); return; }
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    });

    window.alxLabelStudio = { open: open, close: close };
    updateSummary();
    done();
  }

  var rollback = null;
  ready(function () {
    try { init(); } catch (e) {
      if (rollback) rollback();
      done();
      if (window.console) console.error('Label Studio:', e);
    }
  });
})();
